import React from 'react';

import { ParallelLine } from '@paragon/contract-library';
import { Point2D as GeometryLibraryPoint2D } from '@paragon/geometry-library';
import { calculateLine, findLine } from '@paragon/relative-geometry';
import { Point2D } from '@paragon/three-shared';
import { distance2D } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../../components/hooks';
import { findAndSetReferenceLine } from '../../../store/actions/editorActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { EditableDistanceDimension } from '../EditableDistanceDimension';
import { QuickDistanceDimension } from '../QuickDistanceDimension';

const lineNotFoundErrorMessage =
  'Unable to render parallel line dimension—could not find or calculate selected reference line.';
const componentNotFoundErrorMessage = 'Line dimension rendered with no active component design.';

interface Props {
  pointOnLine: Point2D;
  parentLineGuid: string;
  distanceOverride?: number;
  onEdit?: (value: number) => void;
}

function ParallelLineDimensionBase(props: Props) {
  const componentDesign = useAppSelector(getActiveComponentDesign);
  if (componentDesign == null) throw new Error(componentNotFoundErrorMessage);

  const firstSelectionReferenceLine = findLine(props.parentLineGuid, componentDesign.component);
  if (!firstSelectionReferenceLine) throw new Error(lineNotFoundErrorMessage);

  const firstSelectionLine = calculateLine(firstSelectionReferenceLine, componentDesign.component);
  if (!firstSelectionLine) return null;

  const startPoint = GeometryLibraryPoint2D.fromObject(props.pointOnLine).projectOntoLine(firstSelectionLine);

  const start = startPoint.toObject();
  const end = { x: props.pointOnLine.x, y: props.pointOnLine.y };

  const distance = props.distanceOverride ?? distance2D(start.x, start.y, end.x, end.y);

  return props.onEdit ? (
    <EditableDistanceDimension start={start} end={end} distance={distance} onChange={props.onEdit} />
  ) : (
    <QuickDistanceDimension start={start} end={end} distance={distance} />
  );
}

interface ParallelLineDimensionProps {
  line: ParallelLine;
}

export function ParallelLineDimension(props: ParallelLineDimensionProps) {
  const dispatch = useAppDispatch();
  const componentDesign = useAppSelector(getActiveComponentDesign);
  if (componentDesign == null) throw new Error(componentNotFoundErrorMessage);

  const line = calculateLine(props.line, componentDesign.component);
  if (!line || !props.line.lineGuid) return null;

  return (
    <ParallelLineDimensionBase
      parentLineGuid={props.line.lineGuid}
      pointOnLine={line.basePoint}
      distanceOverride={props.line.offset}
      onEdit={(distance) => dispatch(findAndSetReferenceLine({ ...props.line, offset: distance }))}
    />
  );
}
